// Apolo IDE — useDevServer (sprint code-preview-engine, E1-WS3).
//
// React state over the `apolo:devServer:*` IPC. Start/stop the previewed
// project's dev server and reflect its streamed state + output. Outside Electron
// the hook reports 'unsupported' (the panel then offers an iframe-URL fallback).

import { useCallback, useEffect, useState } from 'react';
import {
  isElectron,
  startDevServer,
  stopDevServer,
  onDevServerState,
  onDevServerOutput,
} from '../lib/previewClient.js';

const MAX_LOG = 500; // ring buffer — never grow unbounded

export function useDevServer(sessionId) {
  const supported = isElectron();
  const [state, setState] = useState(supported ? 'idle' : 'unsupported');
  const [url, setUrl] = useState(null);
  const [log, setLog] = useState([]);

  useEffect(() => {
    if (!supported || !sessionId) return undefined;
    const offState = onDevServerState((p) => {
      if (!p || p.sessionId !== sessionId) return;
      setState(p.state);
      if (p.url) setUrl(p.url);
    });
    const offOut = onDevServerOutput((p) => {
      if (!p || p.sessionId !== sessionId) return;
      setLog((prev) => {
        const next = prev.concat({ chunk: p.chunk, stream: p.stream || 'stdout' });
        return next.length > MAX_LOG ? next.slice(next.length - MAX_LOG) : next;
      });
    });
    return () => {
      offState();
      offOut();
    };
  }, [supported, sessionId]);

  const start = useCallback(
    async ({ root, command } = {}) => {
      if (!supported) return { ok: false, reason: 'unsupported' };
      if (!root) return { ok: false, reason: 'no-root' };
      setState('starting');
      setUrl(null);
      setLog([]);
      const res = await startDevServer({ sessionId, root, command });
      if (!res?.ok) {
        // A pre-flight miss — the project has no web dev script (a monorepo
        // whose only dev script relaunches Electron), or the command was
        // blocked — means NOTHING was ever spawned. That's not a crash; it's
        // just "there's no server to auto-start here". Keep the red 'crashed'
        // state ONLY for a real process that spawned and then died (which
        // arrives later via the IPC state stream), so we don't cry wolf.
        const preflightMiss =
          res?.reason === 'no_dev_script' || res?.reason === 'command_blocked';
        setState(preflightMiss ? 'no_script' : 'crashed');
      }
      return res;
    },
    [supported, sessionId],
  );

  const stop = useCallback(async () => {
    if (!supported) return { ok: false, reason: 'unsupported' };
    const res = await stopDevServer({ sessionId });
    setState('idle');
    setUrl(null);
    return res;
  }, [supported, sessionId]);

  return { state, url, log, supported, start, stop };
}
